<template>
  <div v-if="isAuthenticated" class="workbooks" role="alert">
    <div class="workbooks__container">
      <button @click="handleToggleWorkbooks" class="workbooks__close-btn">
        <CloseIcon class="workbooks__close-btn__icon" />
      </button>
      <div class="workbooks__header">
        <h5 class="workbooks__header__title">
          We’re deprecating workbooks.
        </h5>
        <p class="workbooks__header__description">
          Workbooks service is shutting down on December 15, 2023. You can still
          access all content by downloading below.
        </p>
      </div>
      <div class="workbooks__content">
        <div class="workbooks__categories-list">
          <button
            v-for="category in categories"
            :key="category.title"
            @click="$event => updateSelectedCategory(category)"
            :class="[
              'workbooks__categories-list__item',
              category === selectedCategory &&
                'workbooks__categories-list__item--selected',
            ]"
          >
            {{ category.title }}
          </button>
        </div>
        <Dropdown
          class="workbooks__categories-dropdown"
          :options="categories"
          label="title"
          :handleSelection="updateSelectedCategory"
          :selectedOption="selectedCategory.title"
        />
        <div class="workbooks__links-list flat-scroll">
          <a
            v-for="workbook in selectedCategory.workbooks"
            :key="`workbook-${workbook.title}`"
            :href="workbook.link"
            target="_blank"
            rel="noopener"
            class="workbooks__links-list__item"
          >
            <span>
              {{ workbook.title }}
            </span>
            <button
              class="btn btn--secondary btn--default-icon-color"
              v-tooltip="{ content: 'Download content', placement: 'top' }"
            >
              <DownloadIcon />
            </button>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import CloseIcon from '@/assets/icons/close_icon.svg'
import workbooks from '@/static/workbooks'
import DownloadIcon from '@/assets/icons/course/download-icon.svg'
import Dropdown from '@/components/forms/elements/Dropdown.vue'

export default {
  name: 'Workbooks',
  components: {
    CloseIcon: { ...CloseIcon, name: 'CloseIcon' },
    DownloadIcon: { ...DownloadIcon, name: 'DownloadIcon' },
    Dropdown,
  },
  data() {
    return {
      categories: workbooks,
      selectedCategory: workbooks[0],
    }
  },
  computed: {
    ...mapState({
      isAuthenticated: state => state.auth.isAuthenticated,
    }),
    isWorkbooksRoute() {
      return this.$route.name === 'workbooks'
    },
  },
  methods: {
    updateSelectedCategory(category) {
      this.selectedCategory = category
    },
    handleToggleWorkbooks() {
      if (this.isWorkbooksRoute) {
        this.$router.push({ name: 'home' })
      } else {
        this.$emit('toggle-workbooks')
      }
    },
  },
  mounted() {
    if (!this.isAuthenticated) {
      this.handleToggleWorkbooks()
    }
  },
}
</script>

<style lang="scss" scoped>
.workbooks {
  $this: &;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  width: 100%;
  height: 100vh;
  background-color: var(--dark-brighter);
  display: flex;
  justify-content: center;
  align-items: center;

  @include mobile {
    padding: 40px;
  }
  &__container {
    position: relative;
    max-width: 1440px;
    background: var(--glass-mid-gray);
    padding: 40px;
    height: 100%;
    @include mobile {
      height: 90%;
    }
  }
  &__close-btn {
    width: 24px;
    height: 24px;
    position: absolute;
    right: 30px;
    top: 30px;
    background: none;
    border: none;
    cursor: pointer;
    &:hover {
      .workbooks__close-btn__icon {
        fill: var(--orange);
      }
    }
    &__icon {
      width: 12px;
      height: 12px;
      fill: var(--white);
    }
  }
  &__header {
    height: auto;
    &__title {
      color: var(--white);
      font-weight: 700;
      font-size: rem(20px);
      line-height: rem(26px);
      margin: 0;
    }
    &__description {
      color: var(--light-blue-gray);
      font-weight: 450;
      font-size: rem(16px);
      line-height: rem(24px);
      margin: 10px 0 0 0;
    }
  }
  &__content {
    margin-top: 30px;
    height: 80%;
    @include mobile {
      display: flex;
      gap: 40px;
    }
  }
  &__categories-dropdown {
    margin-bottom: 20px;
    @include mobile {
      display: none;
    }
  }
  &__categories-list {
    display: none;

    @include mobile {
      display: flex;
      flex-flow: column;
      gap: 10px;
    }

    &__item {
      border-radius: 40px;
      background-color: transparent;
      border: none;
      padding: 10px 15px;
      color: var(--light-blue-gray);
      font-size: rem(12px);
      line-height: rem(14px);
      font-family: var(--font-book);
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        color: var(--charcoal);
        background: var(--white);
        box-shadow: var(--white-light-box-shadow);
      }
      &:active {
        color: var(--charcoal);
        background: var(--athens-gray);
      }
      &--selected {
        color: var(--charcoal);
        background: var(--white);
      }
    }
  }
  &__links-list {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    gap: 5px;
    height: 100%;
    width: 100%;
    &__item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: var(--glass-mid-gray);
      border-radius: 5px;
      color: var(--white);
      font-family: var(--font-book);
      font-size: rem(14px);
      line-height: rem(20px);
      width: 100%;
      padding: 8px 15px;
      gap: 20px;
      cursor: pointer;

      &:hover {
        background: var(--glass-extra-light);
      }
      &:active {
        background: var(--glass-dark);
      }

      span {
        text-overflow: ellipsis;
        overflow: hidden;
        max-height: 40px;
      }

      button {
        width: 40px;
        svg {
          width: 16px;
          height: 16px;
        }
      }
    }
  }
}
</style>
